import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { UserAvatar } from "common/elements";
import { btnDelay, usePlaySound } from "common/utils";
import { AccountType } from "features/user/types";
import { TOP_GUIDE_CHAPTERS } from "features/builder/guideBookPages";
import { GuideBookId, BuilderGuideBook } from "features/builder/elements";
import { GuideTargetId } from "features/builder/PieceBuilderTopGuideOverlay";
import { DEFAULT_ENABLED_COMMANDS, PieceRankingEntry } from "features/builder/types";
import { MapDetailCommandPanel } from "features/builder/elements/MapDetailCommandPanel";
import { MapDetailCommentPanel } from "features/builder/elements/MapDetailCommentPanel";
import { IconBad, IconFun, IconCool, IconAmazing, IconDifficult } from "features/reaction/asset";
import {
  MockPiece,
  PieceListContext,
  PieceCardAction,
  PieceReaction,
} from "features/builder/pieceList";
import {
  reportPiece,
  reactToPiece,
  getPieceDetail,
  getPieceHints,
  getPieceRanking,
} from "features/builder/api";
import { PieceAuthor } from "./PieceAuthor";
import { BuilderModalAccent } from "./BuilderModalPanel";
import { BuilderReportDialog } from "./BuilderReportDialog";
import { BuilderAccountRequiredDialog } from "./BuilderAccountRequiredDialog";
import { StatusBadge, PieceStatusBadges, getCardVariant, minePublishBtn } from "./MapCard";

const FALLBACK_IMAGE = `${Constants.assetHost}/assets/images/builder_default_display.webp`;

enum DetailTab {
  INFO = "info",
  RANKING = "ranking",
  COMMAND = "command",
  COMMENT = "comment",
}

const TAB_ITEMS: { id: DetailTab; labelKey: string; icon: string }[] = [
  { id: DetailTab.INFO, labelKey: "MSG_BUILDER_MAP_DETAIL_BTN", icon: "info" },
  { id: DetailTab.COMMAND, labelKey: "MSG_BUILDER_DETAIL_TAB_COMMAND", icon: "extension" },
  { id: DetailTab.RANKING, labelKey: "MSG_BUILDER_DETAIL_TAB_RANKING", icon: "leaderboard" },
  { id: DetailTab.COMMENT, labelKey: "MSG_BUILDER_DETAIL_TAB_COMMENT", icon: "chat_bubble" },
];

// 1〜3位のメダル
const RANK_MEDALS = ["👑", "🥈", "🥉"];
const HINT_LEVELS = [
  "MSG_BUILDER_DETAIL_HINT_LV1",
  "MSG_BUILDER_DETAIL_HINT_LV2",
  "MSG_BUILDER_DETAIL_HINT_LV3",
];

// UI 確認用ダミーヒント（本来は API から取得。INFO タブ内で読み取り専用表示）

// リアクション表示（アイコン・順序は creator の ReactionPanel と同じ。easy のみ PNG 画像）
const REACTIONS: { key: keyof PieceReaction; icon: JSX.Element }[] = [
  { key: "difficult", icon: <IconDifficult width={40} height={40} /> },
  {
    key: "easy",
    icon: (
      <img
        alt="*"
        draggable={false}
        className="w-10 h-10"
        src={`${Constants.assetHost}/assets/images/reaction_easy.png`}
      />
    ),
  },
  { key: "cool", icon: <IconCool width={40} height={40} /> },
  { key: "fun", icon: <IconFun width={40} height={40} /> },
  { key: "amazing", icon: <IconAmazing width={40} height={40} /> },
  { key: "bad", icon: <IconBad width={40} height={40} /> },
];

export const MapDetailPage = ({
  piece,
  initialTab,
  context,
  onBack,
  onAction,
  onOpenImage,
}: {
  piece: MockPiece;
  /** メッセージのリンクから開いたときに、最初に見せるタブ */
  initialTab?: string;
  context: PieceListContext;
  onBack: () => void;
  onAction: (action: PieceCardAction) => void;
  onOpenImage: (src: string, alt: string) => void;
}) => {
  const play = usePlaySound();
  const active = useSelector((state: RootState) => state.user.active);
  const [tab, setTab] = useState<DetailTab>(
    (Object.values(DetailTab) as string[]).includes(initialTab ?? "")
      ? (initialTab as DetailTab)
      : DetailTab.INFO,
  );
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  // お気に入りは mock 段階。即時の見た目反映用にローカル状態を持つ（実永続化は後続フェーズ）
  const [liked, setLiked] = useState(piece.is_liked);
  const [likeCount, setLikeCount] = useState(piece.liked);
  const [reportOpen, setReportOpen] = useState(false);
  const [showReportDenied, setShowReportDenied] = useState(false);
  const [ranking, setRanking] = useState<PieceRankingEntry[]>([]);
  const [hints, setHints] = useState<string[]>(["", "", ""]);

  // ヒント・ランキングは API から取得する
  useEffect(() => {
    if (!piece.piece_id) return;
    let canceled = false;
    getPieceHints({ piece_id: piece.piece_id })
      .then((res) => {
        if (!canceled) setHints(res.hints ?? ["", "", ""]);
      })
      .catch(() => undefined);
    getPieceRanking({ piece_id: piece.piece_id, limit: 10 })
      .then((rows) => {
        if (!canceled) setRanking(rows);
      })
      .catch(() => undefined);
    return () => {
      canceled = true;
    };
  }, [piece.piece_id]);
  const [showGuide, setShowGuide] = useState(false);
  const isOfficial = piece.is_official;

  // ピース6種リアクション（公開ピースで操作可。カウントと自分の選択を保持）
  const EMPTY_REACTION: PieceReaction = {
    difficult: 0,
    easy: 0,
    cool: 0,
    fun: 0,
    amazing: 0,
    bad: 0,
  };
  const [reactionCounts, setReactionCounts] = useState<PieceReaction>(
    piece.reaction ?? EMPTY_REACTION,
  );
  const [myReactions, setMyReactions] = useState<string[]>(piece.my_reactions ?? []);
  const canReact = context === PieceListContext.PUBLIC && !!active?.uid;

  // 公開ピースを開いたら最新のカウントと自分のリアクションを取得
  useEffect(() => {
    if (context !== PieceListContext.PUBLIC) return;
    getPieceDetail({ piece_id: piece.piece_id, uid: active?.uid })
      .then((d) => {
        if (d.reaction) setReactionCounts(d.reaction);
        setMyReactions(d.my_reactions ?? []);
      })
      .catch(() => undefined);
  }, [piece.piece_id, active?.uid, context]);

  const handleReact = (key: keyof PieceReaction) => () => {
    if (!canReact || !active?.uid) return;
    play();
    const mine = myReactions.includes(key);
    // 楽観更新
    setMyReactions((prev) => (mine ? prev.filter((k) => k !== key) : [...prev, key]));
    setReactionCounts((prev) => ({ ...prev, [key]: Math.max(0, prev[key] + (mine ? -1 : 1)) }));
    reactToPiece({ uid: active.uid, piece_id: piece.piece_id, reaction: key })
      .then((res) => {
        if (res.reactions) setReactionCounts(res.reactions);
      })
      .catch(() => {
        // 失敗したら戻す
        setMyReactions((prev) => (mine ? [...prev, key] : prev.filter((k) => k !== key)));
        setReactionCounts((prev) => ({
          ...prev,
          [key]: Math.max(0, prev[key] + (mine ? 1 : -1)),
        }));
        toast.warn(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
          </div>,
        );
      });
  };

  const accentBg =
    context === PieceListContext.MINE
      ? "bg-green-100"
      : context === PieceListContext.TRASH
        ? "bg-brown-80"
        : "bg-blue-100";
  const accentBorder =
    context === PieceListContext.MINE
      ? "border-green-100"
      : context === PieceListContext.TRASH
        ? "border-brown-80"
        : "border-blue-100";
  const accentDescBg =
    context === PieceListContext.MINE
      ? "bg-green-100/20"
      : context === PieceListContext.TRASH
        ? "bg-brown-80/20"
        : "bg-blue-100/20";

  const fire = (action: PieceCardAction) => {
    play();
    btnDelay(() => onAction(action));
  };

  const handleBack = () => {
    play();
    btnDelay(() => onBack());
  };

  const handleSelectTab = (id: DetailTab) => () => {
    if (id === tab) return;
    play();
    btnDelay(() => setTab(id));
  };

  const handleTogglePanelExpanded = () => {
    play();
    btnDelay(() => setIsPanelExpanded((v) => !v));
  };

  const handleClosePanelExpanded = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    play();
    btnDelay(() => setIsPanelExpanded(false));
  };

  const handleCopyCode = async () => {
    play();
    try {
      await navigator.clipboard.writeText(piece.inquiry_id);
    } catch {
      // WebView 等でクリップボード API が使えない場合は黙ってスキップ
    }
    toast.success(
      <div>
        <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_COPY")}</p>
      </div>,
    );
  };

  const handleFavorite = () => {
    play();
    setLikeCount((c) => (liked ? c - 1 : c + 1));
    setLiked((v) => !v);
    onAction(PieceCardAction.LIKE);
  };

  // 画像をタップしたら全画面ビューアを開く（cover/capture がある場合のみ）
  // ビューア本体は PieceBuilderTopPage の root レベルにマウントされ、ゲーム機の枠全体を覆う
  const hasImage = Boolean(piece.cover || piece.capture);
  const handleOpenImage = () => {
    if (!hasImage) return;
    play();
    btnDelay(() => onOpenImage(piece.cover ?? piece.capture ?? FALLBACK_IMAGE, piece.name));
  };

  return (
    <div className="flex-col-el relative w-full h-full p-4 gap-3">
      {/* 上部バー */}
      <div
        id={GuideTargetId.DETAIL_HEADER}
        className="flex-row-el items-center justify-between w-full shrink-0 upsilon relative"
      >
        <button
          type="button"
          onClick={handleBack}
          aria-label="back"
          className="type-inherit-font btn btn-blue"
        >
          <div className="flex-row-center gap-1">
            <span className="material-symbols-outlined text-white type-sm">arrow_back</span>
            <p className="type-text type-sm text-white whitespace-nowrap">
              {I18n.t("MSG_BACK_BTN")}
            </p>
          </div>
        </button>

        {/* 中央グループ: アプリコード（マイピース／ゴミばこのみ）＋ つかいかたガイド。
            上部バー中央に絶対配置で横並び表示 */}
        <div className="flex-row-el items-center gap-2 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {context !== PieceListContext.PUBLIC && (
            <div className="flex-row-el items-center gap-2 bg-white rounded-xl px-3 py-1 shadow-md">
              <p className="type-text type-sm text-textcolor-black whitespace-nowrap">
                {I18n.t("MSG_BUILDER_DETAIL_APP_CODE")}
              </p>
              <p className="type-text type-sm text-textcolor-black whitespace-nowrap">
                {piece.inquiry_id}
              </p>
              <button
                type="button"
                onClick={handleCopyCode}
                aria-label="copy-code"
                className="type-inherit-font btn btn-blue !p-1"
              >
                <div className="flex-col-center w-[24px] h-[24px]">
                  <span className="material-symbols-outlined text-white type-sm">content_copy</span>
                </div>
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              play();
              btnDelay(() => setShowGuide(true));
            }}
            aria-label={I18n.t("MSG_BUILDER_TOP_GUIDE_BTN")}
            className="type-inherit-font btn btn-blue"
          >
            <div className="flex-row-center gap-1">
              <span className="material-symbols-outlined text-white type-sm">menu_book</span>
              <p className="type-text type-sm text-white whitespace-nowrap">
                {I18n.t("MSG_BUILDER_TOP_GUIDE_BTN")}
              </p>
            </div>
          </button>
        </div>

        {context === PieceListContext.MINE && (
          <button
            type="button"
            onClick={() => fire(PieceCardAction.TO_TRASH)}
            aria-label="to-trash"
            className="type-inherit-font btn btn-danger"
          >
            <div className="flex-row-center gap-1">
              <span className="material-symbols-outlined text-white type-sm">delete</span>
              <p className="type-text type-sm text-white whitespace-nowrap">
                {I18n.t("MSG_BUILDER_MENU_TO_TRASH")}
              </p>
            </div>
          </button>
        )}

        {context === PieceListContext.PUBLIC && (
          <button
            type="button"
            onClick={() => {
              play();
              // ゲストは通報できない。API を叩いてから断るのではなく、押した時点で理由を出す
              if (active?.account_type === AccountType.GUEST) {
                setShowReportDenied(true);
                return;
              }
              setReportOpen(true);
            }}
            aria-label="report"
            className="type-inherit-font btn btn-danger"
          >
            <div className="flex-row-center gap-1">
              <span className="material-symbols-outlined text-white type-sm">flag</span>
              <p className="type-text type-sm text-white whitespace-nowrap">
                {I18n.t("MSG_BUILDER_MENU_REPORT")}
              </p>
            </div>
          </button>
        )}
      </div>

      {/* 中央：左（画像＋作者）＋ 右（タブ＋パネル） */}
      <div className="flex-row-el flex-1 w-full gap-4 min-h-0">
        {!isPanelExpanded && (
          <div className="flex-col-el w-[360px] shrink-0 min-h-0 gap-3">
            <div
              onClick={handleOpenImage}
              className={`flex-col-el w-full aspect-[4/3] min-h-0 rounded-2xl overflow-hidden bg-beige-60 border-solid border-[4px] shadow-md ${
                hasImage ? "cursor-pointer" : ""
              } ${
                isOfficial ? "border-blue-100 shadow-[0_0_12px_rgba(76,157,241,0.4)]" : accentBorder
              }`}
            >
              <img
                alt={piece.name}
                src={piece.cover ?? piece.capture ?? FALLBACK_IMAGE}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
                }}
                className="w-full h-full object-cover"
              />
            </div>

            {/* 作者（マイ／みんな統一で左下に表示。コピー作品はオリジナル作者→現作者） */}
            {piece.author_name && (
              <div className="flex-col-center w-full shrink-0 bg-white rounded-2xl shadow-md p-2">
                <PieceAuthor piece={piece} />
              </div>
            )}
          </div>
        )}

        <div
          className={`flex-col-el min-w-0 overflow-hidden bg-white ${
            isPanelExpanded
              ? "absolute inset-0 z-30 w-full h-full rounded-2xl shadow-md"
              : "relative flex-1 rounded-2xl shadow-md"
          }`}
        >
          {/* タブバー */}
          <div
            className={`flex-row-el items-center shrink-0 box-border w-full gap-2 bg-beige-60 p-2 invisible-scrollbar ${
              isPanelExpanded
                ? "rounded-t-2xl overflow-x-auto"
                : "rounded-t-2xl border-solid border-4 border-white border-b-0 overflow-x-auto"
            }`}
          >
            {TAB_ITEMS.map(({ id, labelKey, icon }) => {
              const active = id === tab;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={handleSelectTab(id)}
                  className={`type-inherit-font flex-row-center flex-1 min-w-0 gap-1 h-12 px-2 rounded-xl border-solid border-2 cursor-pointer transition-colors ${
                    active
                      ? `${accentBg} ${accentBorder} text-white`
                      : "border-white bg-white text-textcolor-black/70"
                  }`}
                >
                  <span className="material-symbols-outlined type-md shrink-0">{icon}</span>
                  <p className="type-text type-body min-w-0 truncate whitespace-nowrap text-center">
                    {I18n.t(labelKey)}
                  </p>
                </button>
              );
            })}
          </div>

          {/* パネル */}
          <div
            id={GuideTargetId.DETAIL_BODY}
            className={`flex-col-el items-start flex-1 min-h-0 box-border w-full gap-2 bg-white p-4 pb-16 overflow-y-auto invisible-scrollbar ${
              isPanelExpanded ? "rounded-b-2xl" : "border-solid border-4 border-white border-t-0"
            }`}
          >
            {tab === DetailTab.INFO && (
              <>
                <div className="flex-row-el items-center gap-2">
                  <p className="type-text-left type-title text-textcolor-black">{piece.name}</p>
                  {context === PieceListContext.PUBLIC &&
                    (() => {
                      const variant = getCardVariant(piece, context);
                      return variant.statusPill ? (
                        <StatusBadge
                          icon={variant.statusPill.icon}
                          labelKey={variant.statusPill.labelKey}
                          pillClass={variant.statusPill.pillClass}
                          textClass="text-white"
                          iconOnly={false}
                        />
                      ) : null;
                    })()}
                  {context === PieceListContext.MINE && (
                    <PieceStatusBadges piece={piece} iconOnly={false} />
                  )}
                </div>

                {piece.description && (
                  <div className={`flex-col-el items-start w-full rounded-xl p-3 ${accentDescBg}`}>
                    <p className="type-text-left type-body text-textcolor-black w-full">
                      {piece.description}
                    </p>
                  </div>
                )}

                <div
                  className={`flex-row-el flex-wrap items-center gap-4 w-full rounded-xl p-3 ${accentDescBg}`}
                >
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-[#4c9df1] type-body">
                      visibility
                    </span>
                    <p className="type-text type-body text-textcolor-black">{piece.viewed}</p>
                  </div>
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined material-symbols-filled text-[#fc6560] type-body">
                      favorite
                    </span>
                    <p className="type-text type-body text-textcolor-black">{piece.liked}</p>
                  </div>
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-green-100 type-body">
                      play_arrow
                    </span>
                    <p className="type-text type-body text-textcolor-black">{piece.played}</p>
                  </div>
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-[#2bdc98] type-body">
                      military_tech
                    </span>
                    <p className="type-text type-body text-textcolor-black">{piece.cleared}</p>
                  </div>
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-purple type-body">cached</span>
                    <p className="type-text type-body text-textcolor-black">{piece.copied}</p>
                  </div>
                </div>

                <div
                  className={`flex-col-el items-start w-full gap-2 rounded-xl p-3 ${accentDescBg}`}
                >
                  {/* 押せることが見た目で分からなかったので、ひとこと添える */}
                  {canReact && (
                    <div className="flex-row-el items-center gap-1">
                      <span className="material-symbols-outlined type-md text-blue-100">
                        touch_app
                      </span>
                      <p className="type-text-left type-sm text-textcolor-black/70">
                        {I18n.t("MSG_BUILDER_DETAIL_REACTION_HINT")}
                      </p>
                    </div>
                  )}
                  <div className="flex-row-el flex-wrap items-center gap-4 w-full">
                    {REACTIONS.map(({ key, icon }) =>
                      canReact ? (
                        <button
                          key={key}
                          type="button"
                          onClick={handleReact(key)}
                          aria-label={`reaction-${key}`}
                          className={`type-inherit-font flex-row-el items-center gap-1 px-2 py-1 rounded-full box-border border-2 border-solid cursor-pointer transition-transform active:scale-95 ${
                            myReactions.includes(key)
                              ? "bg-blue-100/20 border-blue-100"
                              : "bg-white border-blue-60"
                          }`}
                        >
                          <div className="flex-col-center w-10 h-10">{icon}</div>
                          <p className="type-text type-body text-textcolor-black">
                            {reactionCounts[key]}
                          </p>
                        </button>
                      ) : (
                        <div key={key} className="flex-row-el items-center gap-1">
                          <div className="flex-col-center w-10 h-10">{icon}</div>
                          <p className="type-text type-body text-textcolor-black">
                            {reactionCounts[key]}
                          </p>
                        </div>
                      ),
                    )}
                  </div>
                </div>

                <div
                  className={`flex-row-el flex-wrap items-center gap-x-4 gap-y-1 w-full rounded-xl p-3 ${accentDescBg}`}
                >
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-blue-100 type-body">
                      public
                    </span>
                    <p className="type-text type-body text-textcolor-black">
                      {I18n.t("MSG_BUILDER_DETAIL_PUBLISHED_AT")}
                    </p>
                    <p className="type-text type-body text-textcolor-black">
                      {piece.published_at ? dayjs(piece.published_at).format("YYYY/MM/DD") : "-"}
                    </p>
                  </div>
                  <div className="flex-row-el items-center gap-1">
                    <span className="material-symbols-outlined text-green-100 type-body">
                      update
                    </span>
                    <p className="type-text type-body text-textcolor-black">
                      {I18n.t("MSG_BUILDER_DETAIL_UPDATED_AT")}
                    </p>
                    <p className="type-text type-body text-textcolor-black">
                      {dayjs(piece.updated_at).format("YYYY/MM/DD")}
                    </p>
                  </div>
                </div>

                <p className="type-text-left type-md text-textcolor-black w-full">
                  {I18n.t("MSG_BUILDER_DETAIL_HINT_SECTION")}
                </p>
                <div className="flex-col-el w-full gap-2">
                  {HINT_LEVELS.map((lv, i) => (
                    <div
                      key={lv}
                      className={`flex-col-el items-start w-full rounded-xl p-3 gap-1 ${accentDescBg}`}
                    >
                      <p className="type-text-left type-body text-textcolor-black/70 w-full">
                        {I18n.t(lv)}
                      </p>
                      <p className="type-text-left type-body text-textcolor-black w-full">
                        {hints[i] || I18n.t("MSG_BUILDER_DETAIL_HINT_EMPTY")}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
            {tab === DetailTab.RANKING && (
              <>
                {ranking.length === 0 ? (
                  <p className="type-text type-sm text-textcolor-black/70">
                    {I18n.t("MSG_BUILDER_DETAIL_RANKING_EMPTY")}
                  </p>
                ) : (
                  <>
                    <div className="flex-row-el items-center justify-between w-full rounded-xl px-4 py-3 bg-yellow-100">
                      <div className="flex-col-el items-start">
                        <p className="type-text type-sm text-textcolor-black/70">
                          {I18n.t("MSG_BUILDER_DETAIL_RANKING_WORLD")}
                        </p>
                        <p className="type-text type-title text-textcolor-black">
                          {I18n.t("MSG_BUILDER_DETAIL_RANKING_MOVES_UNIT", {
                            n: ranking[0].moves,
                          })}
                        </p>
                      </div>
                      <p className="type-text type-title">👑</p>
                    </div>

                    <div className="flex-col-el w-full gap-2">
                      {ranking.map((r) => (
                        <div
                          key={r.uid}
                          className={`flex-row-el items-center gap-2 w-full rounded-xl px-3 py-2 ${
                            r.uid === active?.uid
                              ? "border-solid border-2 border-yellow-100 bg-yellow-100/30"
                              : "bg-beige-60"
                          }`}
                        >
                          <p className="type-text type-sm">{RANK_MEDALS[r.rank - 1] ?? ""}</p>
                          <p className="type-text type-sm text-textcolor-black w-6 text-center">
                            {r.rank}
                          </p>
                          <div className="flex-col-center shrink-0">
                            <UserAvatar size={32} icon={r.icon ?? ""} />
                          </div>
                          <p className="type-text type-sm text-textcolor-black flex-1 text-left truncate">
                            {r.uid === active?.uid
                              ? I18n.t("MSG_BUILDER_DETAIL_RANKING_YOU")
                              : (r.name ?? "")}
                          </p>
                          <p className="type-text type-sm text-textcolor-black whitespace-nowrap">
                            {I18n.t("MSG_BUILDER_DETAIL_RANKING_MOVES_UNIT", { n: r.moves })}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {tab === DetailTab.COMMAND && (
              <MapDetailCommandPanel enabledCommands={new Set(DEFAULT_ENABLED_COMMANDS)} />
            )}

            {tab === DetailTab.COMMENT && (
              <MapDetailCommentPanel
                pieceId={piece.piece_id}
                isExpanded={isPanelExpanded}
                piecePublished={piece.published}
                pieceCommentEnabled={piece.enable_comment}
              />
            )}
          </div>

          {/* 拡大/縮小トグル（パネル右下に浮かせる。通常時=ひろげる / 全画面時=とじる） */}
          <button
            type="button"
            onClick={isPanelExpanded ? handleClosePanelExpanded : handleTogglePanelExpanded}
            aria-label={isPanelExpanded ? "close-fullscreen" : "open-fullscreen"}
            className={`type-inherit-font flex-row-el items-center absolute bottom-3 right-3 z-10 gap-1 h-12 px-4 rounded-full shadow-md border-solid border-2 cursor-pointer ${accentBg} ${accentBorder} text-white`}
          >
            <span className="material-symbols-outlined type-md">
              {isPanelExpanded ? "close_fullscreen" : "open_in_full"}
            </span>
            <p className="type-text type-body text-white whitespace-nowrap">
              {I18n.t(
                isPanelExpanded ? "MSG_BUILDER_DETAIL_COLLAPSE" : "MSG_BUILDER_DETAIL_EXPAND",
              )}
            </p>
          </button>
        </div>
      </div>

      {/* 下部アクションバー（カードと同じ並び: 公開 → あそぶ → へんしゅう → コピー） */}
      {!isPanelExpanded && (
        <div className="flex-row-el justify-center items-center w-full gap-3 shrink-0 upsilon">
          {/* 処分されたピースは公開できない。カードと同じく公開ボタンを出さない */}
          {context === PieceListContext.MINE &&
            !piece.banned &&
            (() => {
              const pub = minePublishBtn(piece);
              return (
                <button
                  type="button"
                  disabled={pub.disabled}
                  onClick={() => fire(PieceCardAction.TOGGLE_PUBLISH)}
                  aria-label="toggle-publish"
                  className={`type-inherit-font btn ${pub.className}`}
                >
                  <div className="flex-row-center gap-2">
                    <span className="material-symbols-outlined text-white type-md">{pub.icon}</span>
                    <p className="type-text type-body text-white whitespace-nowrap">
                      {I18n.t(pub.labelKey)}
                    </p>
                  </div>
                </button>
              );
            })()}

          <button
            type="button"
            onClick={() => fire(PieceCardAction.PLAY)}
            aria-label="play"
            className="type-inherit-font btn btn-secondary"
          >
            <div className="flex-row-center gap-2">
              <span className="material-symbols-outlined text-white type-md">play_arrow</span>
              <p className="type-text type-body text-white whitespace-nowrap">
                {I18n.t("MSG_BUILDER_BTN_PLAY")}
              </p>
            </div>
          </button>

          {context === PieceListContext.MINE && (
            <button
              type="button"
              onClick={() => fire(PieceCardAction.EDIT)}
              aria-label="edit"
              className="type-inherit-font btn btn-primary"
            >
              <div className="flex-row-center gap-2">
                <span className="material-symbols-outlined text-white type-md">edit</span>
                <p className="type-text type-body text-white whitespace-nowrap">
                  {I18n.t("MSG_BUILDER_MAP_EDIT_BTN")}
                </p>
              </div>
            </button>
          )}

          {piece.enable_copied && (
            <button
              type="button"
              onClick={() => fire(PieceCardAction.COPY)}
              aria-label="copy"
              className="type-inherit-font btn btn-purple"
            >
              <div className="flex-row-center gap-2">
                <span className="material-symbols-outlined text-white type-md">cached</span>
                <p className="type-text type-body text-white whitespace-nowrap">
                  {I18n.t("MSG_BUILDER_MENU_COPY")}
                </p>
              </div>
            </button>
          )}

          {context === PieceListContext.PUBLIC && (
            <button
              type="button"
              onClick={handleFavorite}
              aria-label="favorite"
              className="type-inherit-font btn btn-white !border-2 !border-solid !border-[#fc6560] transition-transform active:scale-90"
            >
              <div className="flex-row-center gap-2">
                <span
                  className={`material-symbols-outlined ${
                    liked ? "material-symbols-filled" : ""
                  } text-[#fc6560] type-md`}
                >
                  favorite
                </span>
                <p className="type-text type-body text-[#fc6560] whitespace-nowrap">{likeCount}</p>
              </div>
            </button>
          )}
        </div>
      )}

      {/* 通報確認 */}
      {/* ゲストは通報できない。押した時点で理由を出す */}
      <BuilderAccountRequiredDialog
        visible={showReportDenied}
        accent={BuilderModalAccent.ORANGE}
        icon="flag"
        title={I18n.t("MSG_BUILDER_MENU_REPORT")}
        message={I18n.t("MSG_BUILDER_REPORT_NEED_ACCOUNT")}
        close={() => setShowReportDenied(false)}
      />

      <BuilderReportDialog
        visible={reportOpen}
        title={I18n.t("MSG_BUILDER_MENU_REPORT")}
        target={piece.name}
        message={I18n.t("MSG_BUILDER_REPORT_PIECE_CONFIRM")}
        close={() => setReportOpen(false)}
        handleConfirm={(reason, detail) => {
          setReportOpen(false);
          if (!active?.uid) return;
          reportPiece({ piece_id: piece.piece_id, uid: active.uid, reason, detail })
            .then(() =>
              toast.success(
                <div>
                  <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_REPORT_THANKS")}</p>
                </div>,
              ),
            )
            .catch((error) =>
              toast.warn(
                <div>
                  <p className="text text-textcolor-black">
                    {I18n.t(
                      axios.isAxiosError(error) && error.response?.status === 403
                        ? "MSG_BUILDER_REPORT_NEED_ACCOUNT"
                        : "MSG_BUILDER_ACTION_FAILED",
                    )}
                  </p>
                </div>,
              ),
            );
        }}
      />

      <BuilderGuideBook
        open={showGuide}
        book={GuideBookId.TOP}
        chapters={TOP_GUIDE_CHAPTERS}
        onClose={() => setShowGuide(false)}
      />
    </div>
  );
};
